import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Check, Zap, MapPin, Wifi, MoreHorizontal, Building, Shield, Car } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

const iconMap: Record<string, any> = {
  Zap, MapPin, Wifi, Building, Shield, Car, MoreHorizontal,
};

export function ProjectFeatures({ projectId }: { projectId?: string } = {}) {
  const params = useParams<{ id: string }>();
  const id = projectId || params.id;


  const { data: features, isLoading } = useQuery({
    queryKey: ['project-features', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_features' as any)
        .select('*')
        .eq('project_id', id!)
        .order('sort_order');
      if (error) throw error;
      return data as any[];
    },
    enabled: !!id,
  });

  if (isLoading) return <Skeleton className="h-40 rounded-xl" />;
  if (!features || features.length === 0) return null;

  return (
    <section>
      <h2 className="font-display text-lg md:text-xl font-bold mb-3 md:mb-4">Features</h2>
      <div className="border rounded-xl p-4 md:p-6 max-w-full overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
          {features.map((cat: any) => {
            const Icon = iconMap[cat.icon] || Zap;
            return (
              <div key={cat.id} className="min-w-0">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="h-5 w-5 text-primary shrink-0" />
                  <h3 className="font-bold break-words">{cat.category}</h3>
                </div>
                <ul className="space-y-2">
                  {(cat.items || []).map((item: string) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span className="break-words min-w-0">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
